import { useState } from "react";
import { SelectChangeEvent, useTheme } from "@mui/material";
import useMenuNavigationDistribution, {
  MenuNavigationRange,
} from "../../../components/hooks/useMenuNavigationDistribution";
import { MenuNavigationDistributionResponse } from "../../../api/admin";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";

const PRESET_DAYS = [7, 30, 90] as const;

const todayStr = () => new Date().toISOString().slice(0, 10);

const isCustom = (
  range: MenuNavigationRange,
): range is { from: string; to: string } => "from" in range;

const rangeLabel = (range: MenuNavigationRange) =>
  isCustom(range) ? `${range.from} → ${range.to}` : `Last ${range.days} days`;

// Display labels for the raw menu keys the API sends (e.g. "info" -> "Info").
// Add/adjust entries here to change what Ariella sees in the widget. Keys not
// listed fall back to a prettified version of the raw key (see normalizeMenuKey).
const MENU_KEY_LABELS: Record<string, string> = {
  main: "Main Menu",
  collection_info: "Collection Cover Info",
  info: "Klydo Info",
  info_sound: "Klydo Info Sound",
  playback: "Cycle",
};

// Maps a raw menu key from the API to its display label. Unknown keys are
// prettified (separators -> spaces, title-cased) so they still read cleanly.
function normalizeMenuKey(key: string): string {
  const label = MENU_KEY_LABELS[key];
  if (label) return label;
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Per-menu left/right bars.
function MenuNavGrid({ data }: { data: MenuNavigationDistributionResponse }) {
  return (
    <DSBox
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: {
          xxs: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        },
      }}
    >
      {Object.entries(data).map(([menu, dirs]) => {
        const left = dirs.find((d) => d.direction === "left")?.count ?? 0;
        const right = dirs.find((d) => d.direction === "right")?.count ?? 0;
        const total = left + right;
        const leftPct = total > 0 ? Math.round((left / total) * 100) : 0;
        return (
          <DSBox key={menu} sx={{ mb: 2 }}>
            <DSTypography variant="subtitle2" sx={{ mb: "6px", fontSize: 13 }}>
              {normalizeMenuKey(menu)}
            </DSTypography>
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                mb: "2px",
              }}
            >
              <span>&larr; {left}</span>
              <span>{right} &rarr;</span>
            </DSBox>
            <DSBox
              sx={{
                display: "flex",
                height: 6,
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              <DSBox sx={{ width: `${leftPct}%`, bgcolor: "primary.main" }} />
              <DSBox
                sx={{ width: `${100 - leftPct}%`, bgcolor: "secondary.main" }}
              />
            </DSBox>
          </DSBox>
        );
      })}
    </DSBox>
  );
}

function MenuNavigationWidget() {
  const theme = useTheme();
  // The applied range that actually drives the query.
  const [range, setRange] = useState<MenuNavigationRange>({ days: 30 });
  // Whether the custom date-range editor is shown.
  const [customMode, setCustomMode] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading } = useMenuNavigationDistribution(range);

  const selectValue =
    customMode || isCustom(range) ? "custom" : String(range.days);

  const handlePresetChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    if (value === "custom") {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    setRange({ days: Number(value) });
  };

  const applyCustomRange = () => {
    if (from && to && from <= to) {
      setRange({ from, to });
    }
  };

  const canApply = !!from && !!to && from <= to;
  const hasData = !!data && Object.keys(data).length > 0;

  return (
    <DSCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSTypography variant="h6">
          Menu Navigation — Left vs Right
        </DSTypography>
        <DSStack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <DSSelect
            size="small"
            value={selectValue}
            onChange={handlePresetChange}
            sx={{ minWidth: 130 }}
          >
            {PRESET_DAYS.map((d) => (
              <DSMenuItem key={d} value={String(d)}>
                Last {d} days
              </DSMenuItem>
            ))}
            <DSMenuItem value="custom">Custom range</DSMenuItem>
          </DSSelect>
          {selectValue === "custom" && (
            <>
              <DSTextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: to || todayStr() }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSTextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: from, max: todayStr() }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSButton
                size="small"
                variant="contained"
                disabled={!canApply}
                onClick={applyCustomRange}
              >
                Apply
              </DSButton>
            </>
          )}
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1 }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : hasData ? (
          <MenuNavGrid data={data} />
        ) : (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            No navigation data in this range.
          </DSBox>
        )}
      </DSCardContent>
      <DSBox
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5 }}
      >
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          {rangeLabel(range)} · active devices, dev clocks excluded
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default MenuNavigationWidget;
